import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { adminProducts, adminCategories, Product, Category, ProductImage, refreshProductQrCode } from '../../api/admin';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiImage, FiUpload, FiX, FiRefreshCw } from 'react-icons/fi';
import { formatCurrency } from '../../utils/currency';
import QrCodeField from '../../components/Admin/QrCodeField';
import ProductCakeOptionsPanel from '../../components/Admin/ProductCakeOptionsPanel';
import { resolveErrorBanner, ErrorBannerState, getErrorDetail } from '../../utils/errorDetail';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 12px;
  color: #6b7280;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 36px;
  border: 1px solid #E8DDD0;
  border-radius: 8px;
  font-size: 14px;
  width: 250px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const ProductCard = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImageDisplay = styled.div<{ imageUrl?: string }>`
  position: relative;
  width: 100%;
  height: 200px;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : '#f3f4f6'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 48px;
`;

const ImageCount = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ImageIndicators = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
`;

const ImageDot = styled.div<{ active?: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const ProductInfo = styled.div`
  padding: 16px;
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1C1511;
  margin-bottom: 8px;
`;

const ProductPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #D4A017;
  margin-bottom: 8px;
`;

const ProductCategory = styled.div`
  font-size: 12px;
  color: #6B5E4E;
  margin-bottom: 12px;
`;

const ProductActiveBadge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 3px 8px;
  background: ${props => props.active ? '#d1fae5' : '#f3f4f6'};
  color: ${props => props.active ? '#065f46' : '#6b7280'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button<{ variant?: 'edit' | 'delete' | 'reactivate' }>`
  padding: 8px;
  background: ${props => props.variant === 'delete' ? '#fee2e2' : props.variant === 'reactivate' ? '#d1fae5' : 'rgba(212, 160, 23, 0.15)'};
  color: ${props => props.variant === 'delete' ? '#dc2626' : props.variant === 'reactivate' ? '#065f46' : '#1C1511'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'delete' ? '#fecaca' : props.variant === 'reactivate' ? '#a7f3d0' : 'rgba(212, 160, 23, 0.3)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #FAF8F3;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6B5E4E;
  cursor: pointer;

  &:hover {
    color: #1C1511;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Places an EN FormGroup next to its paired ZH (Chinese) FormGroup, e.g. for
// Product Name / Product Name (Chinese). Reuses FormGroup rather than
// introducing new spacing rules.
const FormRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CheckboxHint = styled.small`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #7a6f68;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #1C1511;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #E8DDD0;
  color: #6B5E4E;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #d9cfc4;
  }
`;

const SubmitButton = styled(Button)`
  margin: 0;
`;

const ImageSection = styled.div`
  margin-bottom: 20px;
`;

const ImageUploadArea = styled.div<{ isDragActive?: boolean }>`
  border: 2px dashed ${props => props.isDragActive ? '#D4A017' : '#E8DDD0'};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  margin-bottom: 12px;
  background: ${props => props.isDragActive ? 'rgba(212, 160, 23, 0.08)' : 'transparent'};
  transition: all 0.2s ease;
`;

const ImageUrlInput = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const AddUrlButton = styled.button`
  padding: 8px 16px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #c49015;
  }
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #E8DDD0;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #E8DDD0;
  color: #6B5E4E;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #d9cfc4;
    color: #1C1511;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;


const AdminProducts: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<Product>({
    name: '',
    name_zh: '',
    description: '',
    description_zh: '',
    category_id: '',
    base_price: 0,
    preparation_time_hours: 24,
    active: true,
    qr_enabled: false,
    customizable: false,
    loyalty_points_rate: null,
    allergies: [],
    images: [],
  });
  const [imageUrl, setImageUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [qrImageSrc, setQrImageSrc] = useState('');
  const [qrImageFailed, setQrImageFailed] = useState(false);
  const [qrRefreshing, setQrRefreshing] = useState(false);
  const [qrLoadError, setQrLoadError] = useState<ErrorBannerState | null>(null);
  const [qrRefreshError, setQrRefreshError] = useState<ErrorBannerState | null>(null);
  const [saveError, setSaveError] = useState<ErrorBannerState | null>(null);
  const [reactivateError, setReactivateError] = useState<ErrorBannerState | null>(null);
  const [deleteError, setDeleteError] = useState<ErrorBannerState | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  const handleExportQrPdf = async () => {
    setExporting(true);
    setExportError(false);
    try {
      const blob = await adminProducts.exportQrPdf();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `products-qr-export-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  const { data: rawProducts, isLoading: productsLoading, refetch: refetchProducts } = useQuery({
    queryKey: ['adminProducts'],
    queryFn: adminProducts.getAll,
  });
  const products = Array.isArray(rawProducts) ? rawProducts : [];

  const { data: rawAdminCategories } = useQuery({
    queryKey: ['adminCategories'],
    queryFn: adminCategories.getAll,
  });
  const categories = Array.isArray(rawAdminCategories) ? rawAdminCategories : [];

  const createMutation = useMutation({
    mutationFn: adminProducts.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      closeModal();
    },
    onError: (err: any) => {
      setSaveError({ raw: getErrorDetail(err), fallbackKey: 'admin.products.errors.addFailed' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, product }: { id: string; product: Product }) =>
      adminProducts.update(id, product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      closeModal();
    },
    onError: (err: any) => {
      setSaveError({ raw: getErrorDetail(err), fallbackKey: 'admin.products.errors.updateFailed' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: adminProducts.delete,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      refetchProducts();
    },
    onError: (err: any) => {
      setDeleteError({ raw: getErrorDetail(err), fallbackKey: 'admin.products.errors.deleteFailed' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (product: Product) =>
      adminProducts.update(product.id as string, { ...product, active: true }),
    onSuccess: async () => {
      setReactivateError(null);
      await queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      refetchProducts();
    },
    onError: (err: any) => {
      setReactivateError({ raw: getErrorDetail(err), fallbackKey: 'admin.products.errors.reactivateFailed' });
    },
  });

  const openModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      // Legacy per-product sizes are not edited here (Cake Options replace them).
      const { sizes: _legacySizes, ...productWithoutSizes } = product;
      setFormData({
        ...productWithoutSizes,
        // Ensure allergies are always strings, not objects
        allergies: product.allergies?.map((allergy: any) =>
          typeof allergy === 'string' ? allergy : allergy.allergy_type
        ) || [],
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        name_zh: '',
        description: '',
        description_zh: '',
        category_id: '',
        base_price: 0,
        preparation_time_hours: 24,
        active: true,
        qr_enabled: false,
        customizable: false,
        loyalty_points_rate: null,
        allergies: [],
        images: [],
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setImageUrl('');
    setQrLoadError(null);
    setQrRefreshError(null);
    setSaveError(null);
    setQrImageFailed(false);
    setQrRefreshing(false);
  };

  // The QR image endpoint (GET /admin/products/{id}/qr-image) is admin-gated
  // via a Bearer token header (see backend/app/api/admin.py + auth.py's
  // OAuth2PasswordBearer), so a plain <img src="..."> can't authenticate --
  // there is no cookie-based fallback and no other admin-gated image in this
  // codebase to mirror (regular product photos are unauthenticated static/S3
  // URLs). Fetch the PNG via the authenticated axios client and hand
  // QrCodeField a blob: object URL instead.
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadQrImage = async () => {
      if (isModalOpen && editingProduct?.id && formData.qr_enabled && formData.qr_token) {
        setQrImageFailed(false);
        setQrImageSrc('');
        try {
          const blob = await adminProducts.getQrImage(editingProduct.id);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setQrImageSrc(objectUrl);
          setQrLoadError(null);
        } catch (err) {
          console.error('Failed to load QR code image:', err);
          if (!cancelled) {
            setQrImageSrc('');
            setQrLoadError({ raw: null, fallbackKey: 'admin.products.errors.qrImageLoadFailed' });
            setQrImageFailed(true);
          }
        }
      } else {
        setQrImageSrc('');
        setQrImageFailed(false);
      }
    };

    loadQrImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isModalOpen, editingProduct?.id, formData.qr_enabled, formData.qr_token]);

  const handleRefreshQrCode = async () => {
    if (!editingProduct?.id || qrRefreshing) return;
    setQrRefreshing(true);
    setQrRefreshError(null);
    try {
      const updated = await refreshProductQrCode(editingProduct.id);
      // Update just the token from the response so the image reloads via the
      // effect above, without a full product list re-fetch.
      setFormData(prev => ({ ...prev, qr_token: updated.qr_token }));
    } catch (err) {
      console.error('Failed to refresh QR code:', err);
      setQrRefreshError({ raw: null, fallbackKey: 'admin.products.errors.qrRefreshFailed' });
      try {
        const currentProducts = await adminProducts.getAll();
        const current = currentProducts.find((p: any) => p.id === editingProduct?.id);
        if (current) {
          setFormData(prev => ({ ...prev, qr_token: current.qr_token }));
        } else {
          setQrImageSrc('');
        }
      } catch (reconcileErr) {
        console.error('Failed to reconcile QR code state after refresh error:', reconcileErr);
        setQrImageSrc('');
      }
    } finally {
      setQrRefreshing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    // Upload base64 images first and get URLs
    const uploadedImages = [];
    if (formData.images) {
      console.log('Processing images:', formData.images.length);
      for (const image of formData.images) {
        console.log('Image URL preview:', image.image_url.substring(0, 50));
        if (image.image_url.startsWith('data:')) {
          try {
            console.log('Uploading base64 image...');
            const result = await adminProducts.uploadBase64Image(image.image_url);
            console.log('Upload result:', result);
            uploadedImages.push({
              ...image,
              image_url: result.url, // Use relative URL from backend
            });
          } catch (error) {
            console.error('Failed to upload image:', error);
            // Keep the original if upload fails
            uploadedImages.push(image);
          }
        } else {
          // Keep URLs that are already uploaded
          console.log('Keeping existing URL:', image.image_url);
          uploadedImages.push(image);
        }
      }
    }

    // Update formData with uploaded image URLs
    const productData = {
      ...formData,
      images: uploadedImages,
    };

    console.log('Final product data with images:', productData);

    if (editingProduct?.id) {
      console.log('Updating product:', editingProduct.id);
      updateMutation.mutate({ id: editingProduct.id, product: productData });
    } else {
      console.log('Creating new product');
      createMutation.mutate(productData);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteError(null);
    if (window.confirm(t('admin.products.deactivateConfirm'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleAllergyChange = (allergy: string) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies?.includes(allergy)
        ? prev.allergies.filter(a => a !== allergy)
        : [...(prev.allergies || []), allergy],
    }));
  };

  const handleAddImageUrl = () => {
    if (imageUrl.trim()) {
      const newImage: ProductImage = {
        image_url: imageUrl.trim(),
        is_primary: formData.images?.length === 0,
        sort_order: formData.images?.length || 0,
      };
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), newImage],
      }));
      setImageUrl('');
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index).map((img, i) => ({
        ...img,
        is_primary: i === 0,
        sort_order: i,
      })),
    }));
  };

  const processFiles = (files: FileList) => {
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newImage: ProductImage = {
            image_url: reader.result as string,
            is_primary: formData.images?.length === 0,
            sort_order: formData.images?.length || 0,
          };
          setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), newImage],
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      processFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || t('admin.products.uncategorized');
  };

  if (productsLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.products.title')}</Title>
        <Actions>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder={t('admin.products.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          <Button
            type="button"
            data-testid="export-products-qr-pdf"
            onClick={handleExportQrPdf}
            disabled={exporting}
          >
            {exporting ? t('admin.products.exportQrPdfLoading') : t('admin.products.exportQrPdfButton')}
          </Button>
          <Button onClick={() => openModal()}>
            <FiPlus />
            {t('admin.products.addButton')}
          </Button>
        </Actions>
      </Header>

      {exportError && (
        <ErrorMessage data-testid="export-products-qr-error">{t('admin.products.exportQrPdfError')}</ErrorMessage>
      )}
      {reactivateError && <ErrorMessage>{resolveErrorBanner(reactivateError, t)}</ErrorMessage>}
      {deleteError && <ErrorMessage>{resolveErrorBanner(deleteError, t)}</ErrorMessage>}

      <ProductsGrid>
        {filteredProducts.map((product) => (
          <ProductCard key={product.id}>
            <ProductImageDisplay imageUrl={product.images?.[0]?.image_url}>
              {!product.images?.[0] && <FiImage />}
              {product.images && product.images.length > 1 && (
                <>
                  <ImageCount title={t('admin.products.imagesUploadedCount', { count: product.images.length })}>
                    <FiImage size={14} />
                    {product.images.length}
                  </ImageCount>
                  <ImageIndicators>
                    {product.images && product.images.slice(0, 5).map((_: any, index: number) => (
                      <ImageDot key={index} active={index === 0} />
                    ))}
                  </ImageIndicators>
                </>
              )}
            </ProductImageDisplay>
            <ProductInfo>
              <ProductName>{product.name}</ProductName>
              <ProductPrice>{formatCurrency(product.base_price)}</ProductPrice>
              <ProductCategory>{getCategoryName(product.category_id)}</ProductCategory>
              <ProductActiveBadge active={product.active !== false}>
                {product.active !== false ? t('admin.products.statusActive') : t('admin.products.statusInactive')}
              </ProductActiveBadge>
              <ProductActions>
                <IconButton variant="edit" title={t('admin.products.editButtonTitle')} onClick={() => openModal(product)}>
                  <FiEdit2 />
                </IconButton>
                {product.active !== false ? (
                  <IconButton
                    variant="delete"
                    title={t('admin.products.deactivateButtonTitle')}
                    disabled={deleteMutation.isPending}
                    onClick={() => product.id && handleDelete(product.id)}
                  >
                    <FiTrash2 />
                  </IconButton>
                ) : (
                  <IconButton
                    variant="reactivate"
                    title={t('admin.products.reactivateButtonTitle')}
                    disabled={reactivateMutation.isPending}
                    onClick={() => { setReactivateError(null); product.id && reactivateMutation.mutate(product); }}
                  >
                    <FiRefreshCw />
                  </IconButton>
                )}
              </ProductActions>
            </ProductInfo>
          </ProductCard>
        ))}
      </ProductsGrid>

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{editingProduct ? t('admin.products.modalEditTitle') : t('admin.products.modalAddTitle')}</ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.products.nameLabel')}</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.products.nameZhLabel')}</Label>
                <Input
                  type="text"
                  value={formData.name_zh ?? ''}
                  onChange={(e) => setFormData({ ...formData, name_zh: e.target.value })}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>{t('admin.products.descriptionLabel')}</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.products.descriptionZhLabel')}</Label>
                <TextArea
                  value={formData.description_zh ?? ''}
                  onChange={(e) => setFormData({ ...formData, description_zh: e.target.value })}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('admin.products.categoryLabel')}</Label>
              <Select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                required
              >
                <option value="">{t('admin.products.selectCategoryOption')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.products.basePriceLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.products.preparationTimeLabel')}</Label>
              <Input
                type="number"
                min="1"
                value={formData.preparation_time_hours}
                onChange={(e) => setFormData({ ...formData, preparation_time_hours: parseInt(e.target.value) })}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.products.qrCodeLabel')}</Label>
              <QrCodeField
                qrEnabled={formData.qr_enabled ?? false}
                qrToken={formData.qr_token}
                onQrEnabledChange={(enabled) => setFormData({ ...formData, qr_enabled: enabled })}
                qrImageUrl={qrImageSrc}
                onRefresh={handleRefreshQrCode}
                itemActive={formData.active}
                isRefreshing={qrRefreshing}
                failed={qrImageFailed}
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.customizable ?? false}
                  onChange={(e) => setFormData({ ...formData, customizable: e.target.checked })}
                />
                {t('admin.products.customizableLabel')}
              </CheckboxLabel>
              <CheckboxHint>{t('admin.products.customizableHint')}</CheckboxHint>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.products.loyaltyPointsRateLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={t('admin.products.loyaltyPointsRatePlaceholder')}
                value={formData.loyalty_points_rate ?? ''}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  setFormData({
                    ...formData,
                    loyalty_points_rate: rawValue === '' ? null : parseFloat(rawValue),
                  });
                }}
              />
            </FormGroup>

            {editingProduct?.id && (
              <FormGroup>
                <ProductCakeOptionsPanel productId={editingProduct.id} />
              </FormGroup>
            )}

            <FormGroup>
              <Label>{t('admin.products.imagesLabel')}</Label>
              <ImageSection>
                <ImageUrlInput>
                  <Input
                    type="url"
                    placeholder={t('admin.products.imageUrlPlaceholder')}
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                  />
                  <AddUrlButton type="button" onClick={handleAddImageUrl}>
                    {t('admin.products.addUrlButton')}
                  </AddUrlButton>
                </ImageUrlInput>

                <ImageUploadArea
                  isDragActive={isDragActive}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FileInput
                    type="file"
                    id="imageUpload"
                    accept="image/*"
                    multiple
                    onChange={handleFileUpload}
                  />
                  <UploadButton htmlFor="imageUpload">
                    <FiUpload />
                    {t('admin.products.uploadImagesButton')}
                  </UploadButton>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                    {t('admin.products.dragDropHint')}
                  </div>
                </ImageUploadArea>

                {formData.images && formData.images.length > 0 && (
                  <ImagePreviewContainer>
                    {formData.images.map((image, index) => (
                      <ImagePreview key={index}>
                        <PreviewImage
                          src={
                            image.image_url.startsWith('data:') || image.image_url.startsWith('http')
                              ? image.image_url
                              : `${(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '')}${image.image_url}`
                          }
                          alt={t('admin.products.imageAlt', { index: index + 1 })}
                        />
                        <RemoveImageButton
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <FiX size={12} />
                        </RemoveImageButton>
                      </ImagePreview>
                    ))}
                  </ImagePreviewContainer>
                )}
              </ImageSection>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.products.allergiesLabel')}</Label>
              <CheckboxGroup>
                {['nuts', 'gluten', 'dairy', 'eggs', 'soy', 'shellfish'].map((allergy) => (
                  <CheckboxLabel key={allergy}>
                    <input
                      type="checkbox"
                      checked={formData.allergies?.includes(allergy) || false}
                      onChange={() => handleAllergyChange(allergy)}
                    />
                    {t(`allergy.${allergy}`)}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                {t('admin.products.activeLabel')}
              </CheckboxLabel>
            </FormGroup>

            {saveError && (
              <ErrorMessage>{resolveErrorBanner(saveError, t)}</ErrorMessage>
            )}
            {qrLoadError && (
              <ErrorMessage>{resolveErrorBanner(qrLoadError, t)}</ErrorMessage>
            )}
            {qrRefreshError && (
              <ErrorMessage>{resolveErrorBanner(qrRefreshError, t)}</ErrorMessage>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeModal}>{t('admin.products.cancelButton')}</CancelButton>
              <SubmitButton type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending)
                  ? (editingProduct ? t('admin.products.savingButton') : t('admin.products.addingButton'))
                  : (editingProduct ? t('admin.products.updateButton') : t('admin.products.addSubmitButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminProducts;